import {
  useEffect,
  useId,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type SetStateAction,
} from 'react';
import {
  cleanName,
  doctorCode,
  doctorsInBrief,
  normaliseName,
  patientCode,
  patientSeenBy,
  sameName,
  type Doctor,
  type Patient,
} from '@/domain';
import {
  DOCTOR_SEARCH_MIN,
  PATIENT_SEARCH_MIN,
  PATIENT_SEARCH_PAGE_SIZE,
  useDoctor,
  useDoctorPatients,
  useDoctorSearch,
  usePatientSearch,
} from '@/application/usecases/useClients';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import type { CaseFormValue } from './caseForm';
import intake from './IntakeScreen.module.css';
import redo from './RedoFields.module.css';
import styles from './ClientFields.module.css';

/**
 * One row of a list: a record to choose, with its ID and what else tells it
 * from another of the same name. The request of 6 October 2026 (third), item
 * 2: the ID is on every row a record is picked from.
 */
interface Suggestion {
  id: string;
  name: string;
  /** The record's ID, `DR-0012` or `PT-00031`. */
  code: string;
  detail: string | null;
}

/** Something the line under a field offers to do about what it says. */
interface HintAction {
  label: string;
  onPress: () => void;
}

/** How many of a doctor's patients the field lists at once: as many as a search lists. */
const PATIENTS_LISTED = PATIENT_SEARCH_PAGE_SIZE;

interface RecordFieldProps {
  /** The input's id, which the label names. */
  inputId: string;
  label: string;
  /** The field's `name`, as the form has always called it. */
  fieldName: string;
  placeholder: string;
  /** The name, as typed or as the chosen record has it. */
  text: string;
  /** A record is chosen and nobody is typing: the line with Change stands where the input was. */
  picked: boolean;
  /** The chosen record's ID, on the picked line. */
  pickedCode: string;
  pickedDetail: string | null;
  changeLabel: string;
  /** Whether the field looks records up at all. Without it, a plain text field. */
  lookup: boolean;
  suggestions: readonly Suggestion[];
  /** Matches beyond the ones listed. */
  more: number;
  listLabel: string;
  /** What the name will be kept as, under the field. */
  hint: string | null;
  /** The other choice the line under the field leaves open, as a button under it. */
  hintAction?: HintAction | null;
  /** What the server said against the field. */
  error: string | null;
  onType: (text: string) => void;
  onChoose: (id: string) => void;
  /** Change was pressed: the record is let go and the name can be typed again. */
  onChangeRecord: () => void;
  /** The field took the focus: the moment its list is first wanted. */
  onOpen?: () => void;
}

/**
 * A name that is looked up as it is typed: the input, the list of matches
 * under it, and the line that stands in for both once a record is chosen. The
 * search, list and picked pattern the redo block has, with its styles.
 *
 * To a screen reader it is a combobox with a list: the arrow keys move through
 * the matches, Enter chooses the one they are on, and Escape puts the list
 * away. Enter with no match highlighted is left to the form, as it was when
 * this was a text field.
 *
 * The list is in the flow of the form and never over it, so no container clips
 * it and it scrolls with the page. A row keeps the focus in the input while it
 * is pressed: a list that closed when the input lost the focus would be gone
 * before the press landed.
 */
function RecordField({
  inputId,
  label,
  fieldName,
  placeholder,
  text,
  picked,
  pickedCode,
  pickedDetail,
  changeLabel,
  lookup,
  suggestions,
  more,
  listLabel,
  hint,
  hintAction = null,
  error,
  onType,
  onChoose,
  onChangeRecord,
  onOpen,
}: RecordFieldProps) {
  const ids = { label: useId(), list: useId(), hint: useId(), problem: useId() };
  const [focused, setFocused] = useState(false);
  // Put away with Escape, until the person types or comes back to the field.
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(-1);

  /*
   * Choosing takes the input away and Change brings it back, so the focus is
   * carried across by hand: to the Change button after a choice, where a
   * keyboard would otherwise be left on nothing, and into the input after
   * Change, so the next thing typed is the name.
   */
  const input = useRef<HTMLInputElement>(null);
  const change = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<'input' | 'change' | null>(null);
  useEffect(() => {
    const next = focusNext.current;
    focusNext.current = null;
    if (next === 'input') input.current?.focus();
    if (next === 'change') change.current?.focus();
  });

  const count = suggestions.length;
  const expanded = lookup && focused && !dismissed && count > 0;
  const activeIndex = expanded && active < count ? active : -1;
  const optionId = (index: number) => `${ids.list}-${String(index)}`;

  function choose(id: string) {
    focusNext.current = 'change';
    setActive(-1);
    onChoose(id);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // A key pressed while a letter is still being composed belongs to the keyboard, not the list.
    if (!lookup || count === 0 || event.nativeEvent.isComposing) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const down = event.key === 'ArrowDown';
      // From no row at all, or from a list Escape put away: the first row going down, the last going up.
      const from = activeIndex >= 0 ? activeIndex : down ? -1 : count;
      setDismissed(false);
      setActive((from + (down ? 1 : -1) + count) % count);
      return;
    }
    if (event.key === 'Enter') {
      const chosen = suggestions[activeIndex];
      if (chosen === undefined) return;
      event.preventDefault();
      choose(chosen.id);
      return;
    }
    if (event.key === 'Escape' && expanded) {
      event.preventDefault();
      setDismissed(true);
      setActive(-1);
    }
  }

  const described = [hint !== null && !picked ? ids.hint : '', error !== null ? ids.problem : '']
    .filter((id) => id !== '')
    .join(' ');

  return (
    <div className={`${intake.field} ${styles.field}`}>
      {picked ? (
        <>
          <span className={intake.l} id={ids.label}>
            {label}
          </span>
          <div
            className={`${redo.picked} ${styles.picked}`}
            role="group"
            aria-labelledby={ids.label}
            aria-describedby={described === '' ? undefined : described}
          >
            {/* The words in one column and Change in its own, so a long name wraps
                beside the button and never pushes it onto a line by itself. */}
            <span className={styles.pickedWords}>
              <span className={styles.pickedName}>{text}</span>
              {pickedCode !== '' && <span className={styles.code}>{pickedCode}</span>}
              {pickedDetail !== null && (
                <span className={`${redo.pickedOf} ${styles.pickedDetail}`}>{pickedDetail}</span>
              )}
            </span>
            <Button
              ref={change}
              variant="text"
              className={styles.change}
              aria-label={changeLabel}
              onClick={() => {
                focusNext.current = 'input';
                onChangeRecord();
              }}
            >
              {strings.intake.change}
            </Button>
          </div>
        </>
      ) : (
        <>
          <label className={intake.l} htmlFor={inputId}>
            {label}
          </label>
          <input
            ref={input}
            id={inputId}
            name={fieldName}
            className={intake.input}
            placeholder={placeholder}
            value={text}
            // The browser's own list of remembered names would sit over this one.
            autoComplete="off"
            role={lookup ? 'combobox' : undefined}
            aria-autocomplete={lookup ? 'list' : undefined}
            aria-expanded={lookup ? expanded : undefined}
            aria-controls={expanded ? ids.list : undefined}
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            aria-invalid={error !== null || undefined}
            aria-describedby={described === '' ? undefined : described}
            onChange={(event) => {
              setDismissed(false);
              setActive(-1);
              onType(event.target.value);
            }}
            onFocus={() => {
              setFocused(true);
              setDismissed(false);
              onOpen?.();
            }}
            onBlur={() => {
              setFocused(false);
              setActive(-1);
            }}
            onKeyDown={onKeyDown}
          />

          {expanded && (
            <ul id={ids.list} role="listbox" aria-label={listLabel} className={redo.results}>
              {suggestions.map((suggestion, index) => (
                <li
                  key={suggestion.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`${redo.result} ${styles.option} ${
                    index === activeIndex ? styles.active : ''
                  }`}
                  // Keeps the focus in the input, so the list is still there for the press.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(suggestion.id)}
                >
                  <span className={styles.optionName}>{suggestion.name}</span>
                  <span className={styles.code}>{suggestion.code}</span>
                  {suggestion.detail !== null && (
                    <span className={styles.optionDetail}>{suggestion.detail}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {expanded && more > 0 && <p className={redo.hint}>{strings.intake.moreMatches(more)}</p>}

          {/* Always there, so a screen reader hears the line arrive rather than a new element. */}
          <span id={ids.hint} className={`${intake.note} ${styles.hint}`} aria-live="polite">
            {hint ?? ''}
          </span>
          {/*
            One button whichever choice it offers, so the focus stays on it
            when pressing it turns it into the way back.
          */}
          {hintAction !== null && (
            <Button variant="text" className={styles.hintAction} onClick={hintAction.onPress}>
              {hintAction.label}
            </Button>
          )}
        </>
      )}

      {error !== null && (
        <span id={ids.problem} className={styles.problem}>
          {error}
        </span>
      )}
    </div>
  );
}

/** A patient as a row of the list: the name, the ID, and the doctors the patient has been to. */
function patientRow(patient: Patient): Suggestion {
  return {
    id: patient.id,
    name: patient.name,
    code: patient.code,
    detail: doctorsOf(patient) || strings.intake.patientNoDoctors,
  };
}

/** The doctors a patient has been to, the first two by name and then how many more. Empty for none. */
function doctorsOf(patient: Patient): string {
  const { shown, more } = doctorsInBrief(patient.doctors);
  return strings.intake.doctorsInBrief(
    shown.map((doctor) => doctor.name),
    more,
  );
}

export interface ClientFieldsProps {
  value: CaseFormValue;
  onChange: Dispatch<SetStateAction<CaseFormValue>>;
  /** What was refused about the doctor and the patient, or null for each that is sound. */
  errors: { doctor: string | null; patient: string | null };
}

/**
 * The doctor and the patient of a case, on the intake form and the edit screen
 * alike. The request of 6 October 2026 (second), items 4 and 5, and the request
 * of 6 October 2026 (third), items 2 and 3.
 *
 * Each is a record, with an ID, a profile and its cases, so each field looks
 * its record up rather than taking whatever is typed as a new name every time.
 * The two are looked up apart: a patient is one record whichever doctors it
 * has been to, so the patient field searches every patient, whether the doctor
 * beside it is chosen, new or not typed yet, and changing the doctor leaves a
 * chosen patient where it was.
 *
 * The doctor:
 * - The field lists the doctors that match what is typed, each with its ID and
 *   clinic. Choosing one ties the case to that record, and the field becomes a
 *   line with the name, the ID, the clinic and Change.
 * - A name typed in full that is exactly one listed doctor's is taken as that
 *   doctor without a tap; the field stays a field and says whose name it is.
 * - A name that is nobody's is kept, and the line under the field says the
 *   case will add it: the server makes the record when the case is saved.
 *
 * The patient:
 * - From two letters the field lists the patients that match, each with its ID
 *   and the doctors it has been to. With a doctor chosen and nothing typed yet
 *   it opens on that doctor's patients. Choosing a row ties the case to that
 *   record.
 * - A name typed in full that is exactly one patient's is taken as that
 *   patient. When the patient has been to the doctor on the case the line says
 *   only that the name is in the system; when not, it says whose record the
 *   case joins. Either way the other choice is offered under it: a different
 *   person of the same name, which makes a record of its own. Two people are
 *   never made one, nor one made two, without somebody deciding it.
 * - A name that more than one patient has is nobody's until one is chosen from
 *   the list, or it is said to be a different person again. The form refuses
 *   the case until then: the name alone would go to the oldest of them.
 *
 * Nothing is looked up, and nothing is chosen on the person's behalf, until
 * they touch a field: a case opened for editing and saved untouched sends
 * neither a doctor nor a patient. Somebody who may not read the records
 * (`seeIdentity`) gets two plain text fields, as before.
 */
export function ClientFields({ value, onChange, errors }: ClientFieldsProps) {
  const ids = { doctor: useId(), patient: useId() };
  const session = useSession();
  const mayLookUp = session.data?.permissions.includes('seeIdentity') === true;

  /*
   * Whether somebody is typing in each field. A record chosen from the list,
   * or the one a case already had, shows as the picked line; a name being
   * typed keeps its input, even once it turns out to be a record's, because a
   * field that became a line under the cursor could not be finished.
   */
  const [typingDoctor, setTypingDoctor] = useState(false);
  const [typingPatient, setTypingPatient] = useState(false);
  const [patientOpened, setPatientOpened] = useState(false);
  // The record that was chosen, kept for its clinic on the picked line.
  const [chosenDoctor, setChosenDoctor] = useState<Doctor | null>(null);

  /* ---- the doctor ---- */

  const doctorText = cleanName(value.dentistName);
  const asked = mayLookUp && typingDoctor ? doctorText : '';
  const search = useDoctorSearch(asked);
  /*
   * The search waits for the typing to pause, and until it has, what it holds
   * is the answer to an earlier text. It says which text its answer is to, and
   * only once that is the text in the field is a name said to be new, or taken
   * to be a listed doctor's.
   */
  const answered =
    asked.length >= DOCTOR_SEARCH_MIN && search.data !== undefined && search.answeredFor === asked;

  const listed = search.data?.items ?? [];
  const beyondList = Math.max(0, (search.data?.total ?? 0) - listed.length);
  const namesakes = listed.filter((doctor) => sameName(doctor.name, doctorText));
  const namesake = namesakes.length === 1 ? (namesakes[0] ?? null) : null;
  const namesakeId = namesake?.id ?? null;

  /*
   * A name typed in full that is exactly one listed doctor's is that doctor,
   * chosen without a tap. With none of that name, or with two, no record is
   * chosen: the name travels as typed, and the line under the field says
   * which of the two it is.
   */
  useEffect(() => {
    if (!answered) return;
    onChange((current) =>
      current.doctorId === namesakeId ? current : { ...current, doctorId: namesakeId },
    );
  }, [answered, namesakeId, onChange]);

  const doctorPicked = value.doctorId !== null && !typingDoctor;
  const known = chosenDoctor !== null && chosenDoctor.id === value.doctorId ? chosenDoctor : null;
  // A case opened for editing names its doctor by id only: the clinic is read.
  const record = useDoctor(mayLookUp && doctorPicked && known === null ? value.doctorId : null);
  const pickedClinic = known !== null ? known.clinic : (record.data?.clinic ?? null);

  let doctorHint: string | null = null;
  if (asked.length >= DOCTOR_SEARCH_MIN) {
    if (search.isError) doctorHint = strings.intake.lookupFailed;
    else if (answered && namesakes.length > 1) doctorHint = strings.intake.doctorSeveral;
    else if (answered && namesake !== null) doctorHint = strings.intake.doctorKnown(namesake.clinic);
    // Not said of a list that was cut short: the doctor may be further down it.
    else if (answered && beyondList === 0) {
      doctorHint = listed.length > 0 ? strings.intake.doctorNewOrListed : strings.intake.doctorNew;
    }
  }

  function typeDoctor(text: string) {
    setTypingDoctor(true);
    onChange((current) =>
      // Still the name of the doctor it was taken to be: a capital or a space is not another doctor.
      current.doctorId !== null && sameName(text, current.dentistName)
        ? { ...current, dentistName: text }
        : { ...current, dentistName: text, doctorId: null },
    );
  }

  function chooseDoctor(id: string) {
    const doctor = listed.find((each) => each.id === id);
    if (doctor === undefined) return;
    setChosenDoctor(doctor);
    setTypingDoctor(false);
    onChange((current) => ({ ...current, dentistName: doctor.name, doctorId: doctor.id }));
  }

  function changeDoctor() {
    setTypingDoctor(true);
    onChange((current) => ({ ...current, doctorId: null }));
  }

  /* ---- the patient ---- */

  const patientText = cleanName(value.patientName);
  const patientPicked = value.patientId !== null && !typingPatient;
  const askedPatient = mayLookUp && typingPatient ? patientText : '';
  // Every patient, whichever doctors they have been to. From two letters, as the search asks.
  const searching = askedPatient.length >= PATIENT_SEARCH_MIN;
  const found = usePatientSearch(askedPatient);
  const patientAnswered =
    searching && found.data !== undefined && found.answeredFor === askedPatient;
  const foundPatients = searching ? (found.data?.items ?? []) : [];
  const beyondFound = Math.max(0, (found.data?.total ?? 0) - foundPatients.length);

  /*
   * Before there is anything to search on, a chosen doctor's own patients:
   * the likeliest names for a case of that doctor, there to tap. Read once the
   * field is opened or typed in, and narrowed by a first letter.
   */
  const doctorId = mayLookUp ? value.doctorId : null;
  const patientsOfDoctor = useDoctorPatients(
    !patientPicked && !searching && (patientOpened || typingPatient) ? doctorId : null,
  );
  const theirs = doctorId === null || searching ? [] : (patientsOfDoctor.data?.items ?? []);
  const wanted = normaliseName(patientText);
  const theirsMatching = theirs.filter((patient) => normaliseName(patient.name).includes(wanted));
  const theirsOffered = theirsMatching.slice(0, PATIENTS_LISTED);

  /*
   * Whose name was typed. One patient of exactly that name is that patient,
   * but only when the page of matches is all of them: past a page there may be
   * another of the name, and the server, which holds them all, decides. Two on
   * the page is two whatever lies beyond it.
   */
  const sameNamed = patientAnswered
    ? foundPatients.filter((patient) => sameName(patient.name, patientText))
    : [];
  const only = beyondFound === 0 && sameNamed.length === 1 ? (sameNamed[0] ?? null) : null;
  const onlyId = only?.id ?? null;
  const several = sameNamed.length > 1;

  /*
   * What the answer comes to, kept on the form: the one patient of that name
   * linked without a tap, or, for a name several have, nobody, and the form
   * told so. Somebody who has said this is a different person is left with
   * that: nothing is linked behind their back.
   */
  const { newPatient, patientId, patientAmbiguous } = value;
  useEffect(() => {
    if (!patientAnswered) return;
    const linked = newPatient ? null : onlyId;
    const ambiguous = !newPatient && several;
    if (patientId === linked && patientAmbiguous === ambiguous) return;
    onChange((current) => ({ ...current, patientId: linked, patientAmbiguous: ambiguous }));
  }, [patientAnswered, onlyId, several, newPatient, patientId, patientAmbiguous, onChange]);

  /** A different person of this name: no record is linked, and the case makes one. */
  const addAsNew: HintAction = {
    label: strings.intake.patientDifferent,
    onPress: () =>
      onChange((current) => ({
        ...current,
        patientId: null,
        newPatient: true,
        patientAmbiguous: false,
      })),
  };

  let patientHint: string | null = null;
  let patientAction: HintAction | null = null;
  if (mayLookUp && typingPatient) {
    if (newPatient) {
      patientHint = strings.intake.patientNew;
      // The way back, while the name is still exactly one patient's.
      if (only !== null) {
        patientAction = {
          label: strings.intake.patientUseExisting(only.code),
          onPress: () =>
            onChange((current) => ({ ...current, patientId: only.id, newPatient: false })),
        };
      }
    } else if (searching && found.isError) {
      patientHint = strings.intake.lookupFailed;
    } else if (several) {
      patientHint = strings.intake.patientSeveral;
      patientAction = addAsNew;
    } else if (only !== null) {
      /*
       * Linked either way, and the way out offered either way. A patient who
       * has been to this doctor is almost always the same person, so the line
       * says no more than that; but a doctor may have two patients of one
       * name, and without the button the second could not be booked in here.
       */
      patientHint = patientSeenBy(only, value.doctorId)
        ? strings.intake.patientKnown(only.code)
        : strings.intake.patientSameName(only.code, doctorsOf(only));
      patientAction = addAsNew;
    } else if (patientAnswered && beyondFound === 0 && sameNamed.length === 0) {
      // Not said of a page that was cut short: the patient may be further down it.
      patientHint =
        foundPatients.length > 0 ? strings.intake.patientNewOrListed : strings.intake.patientNew;
    }
  }

  function typePatient(text: string) {
    setTypingPatient(true);
    onChange((current) => {
      // Still the name it was taken to be: a capital or a space is not another patient.
      const same = sameName(text, current.patientName);
      return {
        ...current,
        patientName: text,
        patientId: same ? current.patientId : null,
        patientAmbiguous: same && current.patientAmbiguous,
        // Typing again takes "a different person" back: the name is looked at afresh.
        newPatient: false,
      };
    });
  }

  function choosePatient(id: string) {
    const patient = [...foundPatients, ...theirs].find((each) => each.id === id);
    if (patient === undefined) return;
    setTypingPatient(false);
    onChange((current) => ({
      ...current,
      patientName: patient.name,
      patientId: patient.id,
      newPatient: false,
      patientAmbiguous: false,
    }));
  }

  function changePatient() {
    setTypingPatient(true);
    onChange((current) => ({
      ...current,
      patientId: null,
      newPatient: false,
      patientAmbiguous: false,
    }));
  }

  /*
   * A refusal is said beside the button that was pressed, a screen away on a
   * phone, and against its field here. The two fields come into view with it,
   * so the person is shown where to look.
   */
  const row = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (errors.doctor === null && errors.patient === null) return;
    row.current?.scrollIntoView?.({ block: 'nearest' });
  }, [errors.doctor, errors.patient]);

  return (
    <div className={intake.row} ref={row}>
      <RecordField
        inputId={ids.doctor}
        label={strings.intake.dentist}
        fieldName="dentistName"
        placeholder={strings.intake.dentistPlaceholder}
        text={value.dentistName}
        picked={doctorPicked}
        pickedCode={doctorCode(value.doctorId)}
        pickedDetail={pickedClinic}
        changeLabel={strings.intake.changeDoctor}
        lookup={mayLookUp}
        suggestions={listed.map((doctor) => ({
          id: doctor.id,
          name: doctor.name,
          code: doctor.code,
          detail: doctor.clinic,
        }))}
        more={beyondList}
        listLabel={strings.intake.doctorsList}
        hint={doctorHint}
        error={errors.doctor}
        onType={typeDoctor}
        onChoose={chooseDoctor}
        onChangeRecord={changeDoctor}
      />
      <RecordField
        inputId={ids.patient}
        label={strings.intake.patient}
        fieldName="patientName"
        placeholder={strings.intake.patientPlaceholder}
        text={value.patientName}
        picked={patientPicked}
        pickedCode={patientCode(value.patientId)}
        pickedDetail={null}
        changeLabel={strings.intake.changePatient}
        lookup={mayLookUp}
        suggestions={(searching ? foundPatients : theirsOffered).map(patientRow)}
        more={searching ? beyondFound : theirsMatching.length - theirsOffered.length}
        listLabel={searching ? strings.intake.patientsList : strings.intake.patientsOfDoctorList}
        hint={patientHint}
        hintAction={patientAction}
        error={errors.patient}
        onType={typePatient}
        onChoose={choosePatient}
        onChangeRecord={changePatient}
        onOpen={() => setPatientOpened(true)}
      />
    </div>
  );
}
